import {
  ComplementoPedidoSic,
  EventoHistorico,
  formatarData,
  RespostaSolicitacao,
  Solicitacao,
  StatusSolicitacao,
  TomHistorico,
} from '../services/solicitacoes.service';
import { dataHoraHistorico, emParagrafos, tamanhoLegivel } from '../services/solicitacao-servico-publica.service';
import { MinhaSolicitacao } from './minhas-solicitacoes.model';

/**
 * O contrato do pedido do e-SIC visto pelo cidadão (change `portal-consulta-pedido-sic` da API):
 * a consulta pública por protocolo, a resposta ao complemento e a lista da área logada. As chaves
 * são as do back-end, em camelCase; os rótulos acentuados já vêm prontos.
 *
 * ⚠️ O contrato não tem dado do solicitante, autor, observação interna nem endereço de arquivo —
 * por isso o portal mostra os anexos só pelo nome.
 */

/** O prefixo do protocolo do e-SIC (`SIC-AAAA-NNNNNN-XXXXX`). */
export const PREFIXO_PROTOCOLO_SIC = 'SIC-';

/** A origem que o item do e-SIC mostra, e o "serviço" dele no filtro da aba. */
export const ORIGEM_PEDIDO_SIC = 'Atendimento eletrônico - e-SIC';

export interface AnexoPedidoSicPublicoResponse {
  nome: string;
  tamanhoBytes: number;
  contentType: string;
  data: string;
}

export interface EventoPedidoSicPublicoResponse {
  data: string;
  tipo: string;
  tipoDescricao: string;
  /** `Cidadao`, `Orgao` ou `Sistema`. */
  origem: string;
  origemDescricao: string;
  mensagem: string | null;
  statusNovo: string | null;
  statusNovoDescricao: string | null;
  anexos: readonly AnexoPedidoSicPublicoResponse[];
}

export interface RespostaPedidoSicPublicoResponse {
  data: string;
  ato: string;
  atoDescricao: string;
  tipo: string;
  tipoDescricao: string;
  fundamentoLegal: string | null;
  fundamentoLegalDescricao: string | null;
  /** Nulo na conclusão sem texto. */
  texto: string | null;
  numeroCiclo: number;
}

export interface ComplementoPendenteSicPublicoResponse {
  solicitacao: string;
  dataLimiteCidadao: string;
  /** Hoje ainda é até o dia da data-limite do cidadão. */
  aceitaResposta: boolean;
  permiteAnexos: boolean;
  quantidadeMaximaAnexos: number | null;
  tamanhoMaximoAnexoMb: number | null;
  /** As chaves do catálogo (`Pdf`, `Jpg`…). */
  formatosAnexo: readonly string[];
  /** As extensões já expandidas (`jpg`, `jpeg`). */
  extensoesAnexo: readonly string[];
}

/** `GET sic/publico/pedido/{protocolo}` e o `200` da resposta ao complemento. */
export interface PedidoSicPublicoResponse {
  protocolo: string;
  tipoSolicitacao: string;
  tipoDescricao: string;
  origemDescricao: string;
  anonimo: boolean;
  assunto: string;
  descricao: string;
  /** A categoria temática. */
  departamento: string | null;
  /** ISO sem fuso. */
  dataAbertura: string;
  /** Uma das dez chaves do back-end. */
  status: string;
  statusDescricao: string;
  prazoDias: number;
  prazoUnidade: string;
  prazoUnidadeDescricao: string;
  dataLimite: string;
  prazoSuspenso: boolean;
  /** Sem ciclo vigente: respondido, concluído ou cancelado. */
  encerrado: boolean;
  anexos: readonly AnexoPedidoSicPublicoResponse[];
  /** Em ordem cronológica. */
  historico: readonly EventoPedidoSicPublicoResponse[];
  /** Da mais antiga para a mais recente. */
  respostas: readonly RespostaPedidoSicPublicoResponse[];
  complementoPendente: ComplementoPendenteSicPublicoResponse | null;
  sujeitoARecurso: boolean;
  dataLimiteRecurso: string | null;
  justificativaObrigatoria: boolean;
}

/** Um item de `GET sic/cidadao/pedido`, e o `200` do cancelamento. */
export interface MeuPedidoSicResponse extends PedidoSicPublicoResponse {
  /** Só em `Cancelado`. */
  motivoCancelamento: string | null;
  podeCancelar: boolean;
  podeContestar: boolean;
}

/** Os quatro motivos da contestação, pela chave exata do back-end, com o rótulo do CDS-33. */
export const MOTIVOS_CONTESTACAO_SIC: readonly { chave: string; rotulo: string }[] = [
  { chave: 'RespostaIncompleta', rotulo: 'Resposta incompleta' },
  { chave: 'InformacoesIncorretas', rotulo: 'Informações incorretas' },
  { chave: 'NaoAtendeSolicitacao', rotulo: 'Não atende à solicitação' },
  { chave: 'Outros', rotulo: 'Outros' },
];

/** O corpo de `POST sic/cidadao/pedido/{protocolo}/contestacao`. */
export interface ContestacaoPedidoSicPayload {
  motivo: string;
  motivoOutro: string | null;
  justificativa: string | null;
}

/**
 * Os dez status do e-SIC nos cinco grupos de tela (D14): é por eles que o filtro *Status* da aba
 * compartilhada funciona. O selo mostra o rótulo do back-end, não o grupo.
 */
const GRUPO_POR_STATUS_SIC: Readonly<Record<string, StatusSolicitacao>> = {
  Recebido: 'aberto',
  EmAnalise: 'em-andamento',
  EmTramitacao: 'em-andamento',
  Aguardando: 'em-andamento',
  Recurso1Instancia: 'em-andamento',
  Recurso2Instancia: 'em-andamento',
  RecursoAutoridadeSuperior: 'em-andamento',
  Respondido: 'respondido',
  Concluido: 'finalizado',
  Cancelado: 'cancelado',
};

export function grupoStatusSic(status: string): StatusSolicitacao {
  return GRUPO_POR_STATUS_SIC[status] ?? 'em-andamento';
}

const TOM_POR_EVENTO_SIC: Readonly<Record<string, TomHistorico>> = {
  Abertura: 'azul',
  ComplementoSolicitado: 'ambar',
  ComplementoRespondido: 'azul',
  PrazoRetomado: 'ambar',
  PrazoProrrogado: 'ambar',
  Encaminhado: 'azul',
  RespostaRegistrada: 'verde',
  PedidoConcluido: 'verde',
  RecursoRegistrado: 'ambar',
  PedidoCancelado: 'vermelho',
};

/** `2026-11-20T00:00:00` → `20/11/2026`. */
function dataCurta(iso: string): string {
  return formatarData(iso.slice(0, 10));
}

/**
 * A linha *Prazo* do detalhe:
 * - em curso: `Prazo: 20 dias úteis — até 20/11/2026`, com o número e a unidade CONGELADOS no ciclo;
 * - suspenso: o aviso do complemento;
 * - encerrado: vazio, e a linha some.
 */
export function textoPrazoSic(resposta: Pick<PedidoSicPublicoResponse,
  'encerrado' | 'prazoSuspenso' | 'prazoDias' | 'prazoUnidadeDescricao' | 'dataLimite'>): string {
  if (resposta.encerrado) return '';
  if (resposta.prazoSuspenso) return 'Prazo suspenso enquanto aguardamos o seu complemento';

  return `Prazo: ${resposta.prazoDias} ${resposta.prazoUnidadeDescricao.toLowerCase()} — até ${dataCurta(resposta.dataLimite)}`;
}

/** O complemento pendente com as regras de anexo prontas para o card. */
export function paraComplementoSic(complemento: ComplementoPendenteSicPublicoResponse): ComplementoPedidoSic {
  return {
    solicitacao: complemento.solicitacao,
    dataLimite: dataCurta(complemento.dataLimiteCidadao),
    aceitaResposta: complemento.aceitaResposta,
    permiteAnexos: complemento.permiteAnexos,
    quantidadeMaxima: complemento.permiteAnexos ? complemento.quantidadeMaximaAnexos ?? 0 : 0,
    tamanhoMaximoMb: complemento.permiteAnexos ? complemento.tamanhoMaximoAnexoMb ?? 0 : 0,
    formatos: complemento.formatosAnexo.map((formato) => formato.toUpperCase()),
    extensoes: complemento.extensoesAnexo.map((extensao) => extensao.replace(/^\./, '').toLowerCase()),
  };
}

function paraRespostaSic(resposta: RespostaPedidoSicPublicoResponse): RespostaSolicitacao {
  return {
    respondidaEm: dataHoraHistorico(resposta.data),
    paragrafos: resposta.texto ? emParagrafos(resposta.texto) : [],
    tipo: resposta.tipoDescricao,
    fundamento: resposta.fundamentoLegalDescricao ?? undefined,
  };
}

function paraEventoSic(evento: EventoPedidoSicPublicoResponse, indice: number): EventoHistorico {
  return {
    id: `evento-${indice}`,
    rotulo: evento.tipoDescricao,
    tom: TOM_POR_EVENTO_SIC[evento.tipo] ?? 'azul',
    quando: dataHoraHistorico(evento.data),
    descricao: evento.mensagem ?? '',
    anexos: evento.anexos.map((anexo) => ({ nome: anexo.nome, tamanho: tamanhoLegivel(anexo.tamanhoBytes) })),
  };
}

/**
 * O pedido do e-SIC no modelo que a tela de acompanhamento já renderiza. O `status` é o GRUPO
 * (para o filtro e as cores) e `sic.statusDescricao` o rótulo do back-end (para o selo).
 * `servico` é a origem: é o que o filtro *Serviço* da aba oferece.
 */
export function paraSolicitacaoSic(resposta: PedidoSicPublicoResponse): Solicitacao {
  const abertaEm = resposta.dataAbertura.slice(0, 10);

  return {
    protocolo: resposta.protocolo,
    servico: ORIGEM_PEDIDO_SIC,
    descricao: resposta.descricao,
    endereco: '',
    abertaEm,
    abertaAs: resposta.dataAbertura.slice(11, 16),
    status: grupoStatusSic(resposta.status),
    categoria: 'sic',
    tipo: resposta.tipoDescricao,
    prazo: textoPrazoSic(resposta),
    arquivos: resposta.anexos.map((anexo, indice) => ({
      id: `anexo-${indice}`,
      nome: anexo.nome,
      data: dataCurta(anexo.data),
      tamanho: tamanhoLegivel(anexo.tamanhoBytes),
      url: '',
    })),
    historico: resposta.historico.map(paraEventoSic),
    respostas: resposta.respostas.map(paraRespostaSic),
    sic: {
      assunto: resposta.assunto,
      statusDescricao: resposta.statusDescricao,
      departamento: resposta.departamento ?? '',
      complemento: resposta.complementoPendente ? paraComplementoSic(resposta.complementoPendente) : null,
      sujeitoARecurso: resposta.sujeitoARecurso,
      dataLimiteRecurso: resposta.sujeitoARecurso && resposta.dataLimiteRecurso ? dataCurta(resposta.dataLimiteRecurso) : '',
      justificativaObrigatoria: resposta.justificativaObrigatoria,
    },
  };
}

/**
 * O pedido do titular no modelo da aba *Minhas solicitações*: o mesmo do acompanhamento, mais o
 * motivo do próprio cancelamento e as ações que a API decidiu. Os anexos do e-SIC não têm código
 * de download — o portal os mostra só pelo nome.
 */
export function paraMeuPedidoSic(resposta: MeuPedidoSicResponse): MinhaSolicitacao {
  const base = paraSolicitacaoSic(resposta);

  return {
    ...base,
    arquivos: base.arquivos.map((arquivo) => ({ ...arquivo, codigo: 0 })),
    motivoCancelamento: resposta.motivoCancelamento?.trim() || undefined,
    podeCancelar: resposta.podeCancelar,
    podeContestar: resposta.podeContestar,
  };
}

/**
 * Confere UM arquivo contra as regras do departamento que vieram na resposta — o servidor
 * confere de novo, inclusive o tipo real pelos bytes. Devolve a recusa, ou `null`.
 */
export function conferirArquivoSic(arquivo: File, complemento: ComplementoPedidoSic): string | null {
  const extensao = arquivo.name.includes('.') ? arquivo.name.slice(arquivo.name.lastIndexOf('.') + 1).toLowerCase() : '';

  if (!complemento.extensoes.includes(extensao)) {
    return `"${arquivo.name}" não está num formato aceito (${complemento.formatos.join(', ')}).`;
  }

  if (arquivo.size <= 0) return `"${arquivo.name}" está vazio.`;

  if (arquivo.size > complemento.tamanhoMaximoMb * 1024 * 1024) {
    return `"${arquivo.name}" passa do tamanho máximo de ${complemento.tamanhoMaximoMb} MB.`;
  }

  return null;
}
